import React, { forwardRef, useState, useEffect, type MutableRefObject } from 'react'
import { Volume2, AlertCircle } from 'lucide-react'

interface SongAudioPlayerProps {
  audioUrl: string | null
  lyricsSections?: Array<{ start: number; end: number; text: string }>
  currentTimestamp?: number
  onTimeUpdate?: (currentTime: number) => void
  currentPhrase?: string
}

// Error Boundary Component
export class AudioPlayerErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Audio player error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="mb-6 terminal-border p-4 bg-black">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-500 font-mono text-sm">
              Audio not available for this card
            </span>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export const SongAudioPlayer = forwardRef<HTMLAudioElement, SongAudioPlayerProps>(
  ({ audioUrl, lyricsSections, onTimeUpdate, currentPhrase }, ref) => {
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [hasError, setHasError] = useState(false)

    // Reset error state when audio URL changes
    useEffect(() => {
      setHasError(false)
    }, [audioUrl])

    // Handle time updates
    const handleTimeUpdate = () => {
      const audioElement = (ref as MutableRefObject<HTMLAudioElement>)?.current
      if (audioElement) {
        setCurrentTime(audioElement.currentTime)
        onTimeUpdate?.(audioElement.currentTime)
      }
    }

    // Handle metadata loaded
    const handleLoadedMetadata = () => {
      const audioElement = (ref as MutableRefObject<HTMLAudioElement>)?.current
      if (audioElement) {
        setDuration(audioElement.duration)
      }
    }

    // Handle play/pause state (removed - not used in this implementation)

    // Handle audio errors
    const handleError = (e: React.SyntheticEvent<HTMLAudioElement>) => {
      console.error('Audio loading error:', e.currentTarget.error)
      setHasError(true)
    }

    // Format time for display
    const formatTime = (seconds: number) => {
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    // Find current phrase based on timestamp
    const getCurrentPhrase = () => {
      if (!lyricsSections) return currentPhrase || ''

      const current = lyricsSections.find(
        section => currentTime >= section.start && currentTime < section.end
      )
      return current ? current.text : currentPhrase || ''
    }

    if (hasError || !audioUrl) {
      return (
        <div className="mb-6 terminal-border p-4 bg-black">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-500 font-mono text-sm">
              Audio not available for this card
            </span>
          </div>
        </div>
      )
    }

    return (
      <div className="mb-6 terminal-border p-4 bg-black">
        <div className="flex items-center gap-3 mb-2">
          <Volume2 className="w-5 h-5 text-sky-blue" />
          <span className="text-sky-blue font-mono text-sm uppercase">
            [AUDIO PREVIEW]
          </span>
          {duration > 0 && (
            <span className="text-terminal-green font-mono text-xs ml-auto">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          )}
        </div>

        <audio
          ref={ref}
          controls
          className="w-full h-10 audio-player"
          src={audioUrl}
          preload="metadata"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onError={handleError}
        />

        {/* Current phrase display */}
        {(currentPhrase || getCurrentPhrase()) && (
          <div className="text-xs text-terminal-green font-mono mt-2 animate-pulse">
            ▶ {getCurrentPhrase()}
          </div>
        )}
      </div>
    )
  }
)

SongAudioPlayer.displayName = 'SongAudioPlayer'